import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { qk } from '@/lib/queryKeys';
import { addPhDays, displayPhTime, phDateTimeLocal, todayInPH } from '@/lib/phDate';
import { parseTypedAmount } from '@/lib/number';
import { useAuth } from '@/hooks/useAuth';
import { useMembership } from '@/hooks/useMembership';
import { cropPhase, daysBetween, pondAreaM2, prepDayOn, type Crop, type PondFacts } from '@/lib/crop';
import { clearText } from '@/lib/clearToBottom';
import { GRID_PARAMS, isClearToBottom } from '@/lib/waterModel';
import { formatCalMag, formatPair } from '@/lib/pairs';
import { readingToValues } from '@/components/entry/adaptive/entryData';
import { Dialog } from '@/components/ui/Dialog';
import type { WaterCheckResponse, WaterReadingResponse } from '@/types/generated/openapi';
import { Chip, EmptyNote, Kicker, Section, TabLink } from './shared';
import { amountText, headerActionClass } from './format';
import {
  putMilestone,
  prepJournalDays,
  PREP_MILESTONES,
  useApplicationSummary,
  useMilestones,
  usePrepApplications,
  type MilestoneKind,
} from './data';
import { WaterCheckForm } from './WaterCheckForm';
import { StockingFields } from './StockingFields';
import { EMPTY_STOCKING_TEXT, densityText, parseStockingText, type StockingFieldKey, type StockingFieldText } from '@/lib/stockingFields';
import { canEditCheck, checkDay, checkEntries, colorLabel, slotClock, useWaterCheckMutations, useWaterChecks } from './waterChecks';
import { MILESTONE_HINT, STOCKING_HINT, TECHNICIAN_PREP_NOTE } from './prepCopy';

const MILESTONES = PREP_MILESTONES;

const fmt = (d: string) => format(parseISO(d), 'MMM d');

function usePrepReadings(farmId: string, crop: Crop) {
  const start = crop.prep_start_date;
  const end = crop.stocking_date ? addPhDays(crop.stocking_date, -1) : todayInPH();
  return useQuery({
    queryKey: ['readings', farmId, crop.id, 'prep', start, end],
    queryFn: () =>
      api
        .get<{ data: WaterReadingResponse[] }>(`/api/v1/farms/${farmId}/readings`, {
          params: { cycle_id: crop.id, start_date: start!, end_date: end, limit: '100' },
        })
        .then((r) => r.data ?? []),
    enabled: !!start,
  });
}

interface PrestockingTabProps {
  farmId: string;
  pond: PondFacts;
  crop: Crop;
  canSupervise: boolean;
  onMarkStocked: () => void;
}

export function PrestockingTab({ farmId, pond, crop, canSupervise, onMarkStocked }: PrestockingTabProps) {
  const preparing = cropPhase(crop) === 'prep';
  if (!crop.prep_start_date) {
    return (
      <Section labelledBy="ps-none" title="Pre-stocking">
        <EmptyNote>This crop was stocked without a recorded pre-stocking stage.</EmptyNote>
      </Section>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      <MilestonesCard farmId={farmId} crop={crop} canSupervise={canSupervise} />
      <div className="flex flex-wrap items-start gap-5">
        <PrepJournal farmId={farmId} pondId={pond.id} crop={crop} canSupervise={canSupervise} />
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-5">
          <PrepWater farmId={farmId} pondId={pond.id} crop={crop} />
          <PrepSoFar farmId={farmId} crop={crop} />
          {preparing && canSupervise && (
            <section className="flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
              <h2 className="text-base font-semibold text-gray-900">Mark as stocked</h2>
              <p className="text-[13px] text-gray-600">
                Asks for the stocking date and pieces. That day becomes DOC 1, the Stocking, Feeding and Sampling tabs open, and the prep
                journal stays with this crop.
              </p>
              <button type="button" onClick={onMarkStocked} className={cn(headerActionClass(false, 'stock'), 'self-start')}>
                Mark as stocked…
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function MilestonesCard({ farmId, crop, canSupervise }: { farmId: string; crop: Crop; canSupervise: boolean }) {
  const queryClient = useQueryClient();
  const { data: milestones = [] } = useMilestones(farmId, crop.id);
  const { isTechnician } = useMembership();
  const preparing = cropPhase(crop) === 'prep';
  const byKind = new Map(milestones.map((m) => [m.kind, m]));
  const [editing, setEditing] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ kind: MilestoneKind; action: 'done' | 'undo' } | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: qk.milestones.crop(farmId, crop.id) });
    queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
  };

  const save = useMutation({
    mutationFn: ({ kind, patch }: { kind: MilestoneKind | 'stocking'; patch: Record<string, string | null> | null }) =>
      kind === 'stocking'
        ? api.put(`/api/v1/farms/${farmId}/cycles/${crop.id}`, patch)
        : patch === null
          ? api.delete(`/api/v1/farms/${farmId}/cycles/${crop.id}/milestones/${kind}`)
          : putMilestone(queryClient, farmId, crop.id, kind, patch),
    onSuccess: () => {
      setEditing(null);
      setConfirm(null);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message || 'Not saved'),
  });

  return (
    <section aria-label="Pre-stocking milestones" className="flex flex-col gap-3.5 rounded-xl border border-gray-200/80 bg-white px-5 py-4 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Kicker>Pre-stocking milestones</Kicker>
        <span className="text-[13px] text-gray-500">Length is open: stocking happens when the water is ready</span>
      </div>
      <ol className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {MILESTONES.map(({ kind, label }) => {
          const m = byKind.get(kind);
          const done = !!m?.done_on;
          return (
            <li key={kind} className="flex min-w-0 flex-col gap-1.5">
              <Dot done={done} />
              <b className="text-sm">{label}</b>
              {MILESTONE_HINT[kind] && <span className="text-xs leading-snug text-gray-500">{MILESTONE_HINT[kind]}</span>}
              {done && editing === `${kind}:done` ? (
                <DateEdit
                  label={`Done date for ${label}`}
                  initial={m!.done_on!}
                  min={kind === 'water_in' ? undefined : (crop.prep_start_date ?? undefined)}
                  max={crop.stocking_date && crop.stocking_date < todayInPH() ? crop.stocking_date : todayInPH()}
                  busy={save.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(d) => {
                    if (!d) return setEditing(null);
                    const until = m!.done_until && m!.done_until > d ? m!.done_until : null;
                    save.mutate({ kind, patch: { done_on: d, done_until: until } });
                  }}
                />
              ) : done ? (
                <span className="text-[13px] tabular-nums text-gray-600">
                  {fmt(m!.done_on!)}
                  {m!.done_until && m!.done_until !== m!.done_on ? `–${fmt(m!.done_until)}` : ''} · Prep day {prepDayOn(crop, m!.done_on!) ?? '—'}
                  {canSupervise && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        aria-label={`Edit the date ${label} was done`}
                        className="font-semibold text-primary-600"
                        onClick={() => setEditing(`${kind}:done`)}
                      >
                        edit
                      </button>
                      {kind !== 'water_in' && (
                        <>
                          {' · '}
                          <button
                            type="button"
                            aria-label={`Mark ${label} not done`}
                            className="font-semibold text-primary-600"
                            onClick={() => setConfirm({ kind, action: 'undo' })}
                          >
                            undo
                          </button>
                        </>
                      )}
                    </>
                  )}
                </span>
              ) : editing === kind ? (
                <DateEdit
                  label={`Planned date for ${label}`}
                  initial={m?.planned_on ?? ''}
                  min={crop.prep_start_date ?? undefined}
                  busy={save.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(d) => {
                    // A milestone row needs a done or planned date, so clearing the plan removes it.
                    if (d) save.mutate({ kind, patch: { planned_on: d } });
                    else if (m) save.mutate({ kind, patch: null });
                    else setEditing(null);
                  }}
                />
              ) : m?.planned_on || canSupervise ? (
                <span className="text-[13px] tabular-nums text-gray-500">
                  {m?.planned_on ? `Planned ${fmt(m.planned_on)}` : 'Not planned'}
                  {preparing && canSupervise && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        aria-label={`Change the planned date for ${label}`}
                        className="font-semibold text-primary-600"
                        onClick={() => setEditing(kind)}
                      >
                        change
                      </button>
                    </>
                  )}
                </span>
              ) : null}
              {confirm?.kind === kind ? (
                <span role="group" aria-label="Confirm" className="flex flex-wrap items-center gap-1.5 text-[13px]">
                  <span className="text-gray-700">
                    {confirm.action === 'done' ? `${label} done today?` : `Mark ${label} not done?`}
                  </span>
                  <button
                    type="button"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate(
                        confirm.action === 'done'
                          ? { kind, patch: { done_on: todayInPH() } }
                          : // Without a plan the row would hold no date at all, so it goes.
                            { kind, patch: m?.planned_on ? { done_on: null, done_until: null } : null }
                      )
                    }
                    className="min-h-[32px] rounded-md bg-primary-600 px-2.5 text-xs font-semibold text-white hover:bg-primary-700"
                  >
                    Yes
                  </button>
                  <button type="button" onClick={() => setConfirm(null)} className="min-h-[32px] px-1.5 text-xs text-gray-600">
                    No
                  </button>
                </span>
              ) : (
                !done &&
                preparing && (
                  <button
                    type="button"
                    disabled={save.isPending}
                    onClick={() => setConfirm({ kind, action: 'done' })}
                    className="min-h-[32px] self-start rounded-md border border-gray-300 px-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Done today
                  </button>
                )
              )}
            </li>
          );
        })}
        <li className="flex min-w-0 flex-col gap-1.5">
          <Dot done={!!crop.stocking_date} stock />
          <b className="text-sm">Stocking · DOC 1</b>
          <span className="text-xs leading-snug text-gray-500">{STOCKING_HINT}</span>
          {crop.stocking_date ? (
            <span className="text-[13px] tabular-nums text-gray-600">{fmt(crop.stocking_date)}</span>
          ) : editing === 'stocking' ? (
            <DateEdit
              label="Planned stocking date"
              initial={crop.planned_stocking_date ?? ''}
              min={crop.prep_start_date ?? undefined}
              busy={save.isPending}
              onCancel={() => setEditing(null)}
              onSave={(d) => save.mutate({ kind: 'stocking', patch: { planned_stocking_date: d || null } })}
            />
          ) : crop.planned_stocking_date || canSupervise ? (
            <span className="text-[13px] tabular-nums text-gray-500">
              {crop.planned_stocking_date ? `Planned ${fmt(crop.planned_stocking_date)}` : 'Not planned'}
              {preparing && canSupervise && (
                <>
                  {' · '}
                  <button
                    type="button"
                    aria-label="Change the planned stocking date"
                    className="font-semibold text-primary-600"
                    onClick={() => setEditing('stocking')}
                  >
                    change
                  </button>
                </>
              )}
            </span>
          ) : null}
        </li>
      </ol>
      {isTechnician && preparing && <p className="text-[13px] text-gray-600">{TECHNICIAN_PREP_NOTE}</p>}
    </section>
  );
}

function Dot({ done, stock }: { done: boolean; stock?: boolean }) {
  return (
    <span
      role="img"
      aria-label={done ? 'Done' : 'Not done'}
      className={cn(
        'h-3.5 w-3.5 rounded-full border-2',
        done ? 'border-primary-600 bg-primary-600' : stock ? 'border-amber-700 bg-white' : 'border-gray-400 bg-white'
      )}
    />
  );
}

function DateEdit({
  label,
  initial,
  min,
  max,
  busy,
  onSave,
  onCancel,
}: {
  label: string;
  initial: string;
  min?: string;
  max?: string;
  busy: boolean;
  onSave: (d: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <input
        type="date"
        aria-label={label}
        value={value}
        min={min}
        max={max}
        onChange={(e) => setValue(e.target.value)}
        className="min-h-[32px] rounded-md border border-gray-300 px-2 text-[13px]"
      />
      <button type="button" disabled={busy} onClick={() => onSave(value)} className="min-h-[32px] px-1.5 text-[13px] font-semibold text-primary-600">
        Save
      </button>
      <button type="button" onClick={onCancel} className="min-h-[32px] px-1.5 text-[13px] font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900">
        Cancel
      </button>
    </span>
  );
}

function PrepJournal({ farmId, pondId, crop, canSupervise }: { farmId: string; pondId: string; crop: Crop; canSupervise: boolean }) {
  const { data: milestones = [] } = useMilestones(farmId, crop.id);
  const { apps, loading } = usePrepApplications(farmId, crop.id);
  const { checks, loading: checksLoading } = useWaterChecks(farmId, crop.id);
  const preparing = cropPhase(crop) === 'prep';
  const start = crop.prep_start_date!;
  const today = todayInPH();
  const shown = prepJournalDays(crop, milestones, apps, preparing, checks);
  const [dialog, setDialog] = useState<{ editing: WaterCheckResponse | null } | null>(null);
  // A new key per open: a form still fading out from a saved check must not be the one that reopens.
  const [openCount, setOpenCount] = useState(0);
  const openDialog = (editing: WaterCheckResponse | null) => {
    setOpenCount((n) => n + 1);
    setDialog({ editing });
  };

  return (
    <Section
      labelledBy="ps-journal"
      title="Prep journal"
      className="flex-[2_1_520px]"
      action={
        preparing ? (
          <span className="flex items-center gap-3">
            <button type="button" onClick={() => openDialog(null)} className="text-sm font-semibold text-primary-600">
              + Water check
            </button>
            <TabLink pondId={pondId} tab="applications">+ Log application</TabLink>
          </span>
        ) : undefined
      }
    >
      {loading || checksLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : shown.length === 0 ? (
        <EmptyNote>Nothing logged during prep.</EmptyNote>
      ) : (
        <ol className="flex flex-col">
          {shown.map((d) => (
            <li
              key={`${d.day}|${d.date}`}
              className={cn(
                'grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 border-t border-gray-100 py-3 text-sm first:border-t-0',
                d.date === today && preparing && 'rounded-lg border-t-0 bg-blue-50 px-3'
              )}
            >
              <span className="tabular-nums">
                <b>{d.day > 0 ? `Prep ${d.day}` : 'Before water-in'}</b>
                <br />
                <span className="text-gray-500">{d.day > 0 && d.date === today ? `Today, ${fmt(d.date)}` : d.day > 0 ? fmt(d.date) : `to ${fmt(addPhDays(start, -1))}`}</span>
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                {d.milestones.map((m) => (
                  <Chip key={m} tone="prep" className="self-start">
                    {m}
                  </Chip>
                ))}
                {d.checks.map((c) => (
                  <WaterCheckRow
                    key={c.id}
                    farmId={farmId}
                    cropId={crop.id}
                    check={c}
                    canSupervise={canSupervise}
                    showDate={d.day === 0}
                    onEdit={() => openDialog(c)}
                  />
                ))}
                {d.applications.length > 0 ? (
                  <span>
                    {d.applications.map((a, i) => (
                      <span key={a.id}>
                        {i > 0 && ' · '}
                        <b>{a.product_name}</b> {amountText(a.amount, a.unit)}
                        {d.day === 0 && <span className="text-gray-500"> ({fmt(a.application_date)})</span>}
                      </span>
                    ))}
                    {d.applications.some((a) => a.reason) && (
                      <span className="block text-gray-500">{[...new Set(d.applications.map((a) => a.reason).filter(Boolean))].join(' · ')}</span>
                    )}
                  </span>
                ) : d.milestones.length === 0 && d.checks.length === 0 ? (
                  <span className="text-gray-500">Nothing logged yet today.</span>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
      <WaterCheckDialog open={!!dialog} onClose={() => setDialog(null)} openKey={openCount} farmId={farmId} crop={crop} editing={dialog?.editing ?? null} />
    </Section>
  );
}

function WaterCheckRow({
  farmId,
  cropId,
  check,
  canSupervise,
  showDate,
  onEdit,
}: {
  farmId: string;
  cropId: string;
  check: WaterCheckResponse;
  canSupervise: boolean;
  showDate: boolean;
  onEdit: () => void;
}) {
  const { user } = useAuth();
  const { remove } = useWaterCheckMutations(farmId, cropId);
  const [confirming, setConfirming] = useState(false);
  const entries = checkEntries(check);
  const color = colorLabel(check.water_color);
  const time = displayPhTime(check.measured_at);
  const when = showDate ? `${fmt(checkDay(check))}, ${time}` : time;
  return (
    <div className="flex flex-col gap-0.5">
      <span>
        <Chip tone="neutral" className="mr-2 align-middle">
          Water check · {when}
        </Chip>
        <span className="tabular-nums">{entries.map((e) => `${e.short} ${e.text}`).join(' · ')}</span>
        {color && <span className="text-gray-500"> · {color}</span>}
      </span>
      {check.notes && <span className="text-gray-500">{check.notes}</span>}
      {canEditCheck(check, canSupervise, user?.id) && (
        <span className="flex flex-wrap items-center gap-1.5 text-[13px]">
          <button type="button" aria-label={`Edit the ${time} water check`} className="font-semibold text-primary-600" onClick={onEdit}>
            edit
          </button>
          {canSupervise &&
            (confirming ? (
              <span role="group" aria-label="Confirm delete" className="flex items-center gap-1.5">
                <span className="text-gray-700">Delete this check?</span>
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(check.id, { onSettled: () => setConfirming(false) })}
                  className="min-h-[32px] rounded-md bg-red-600 px-2.5 text-xs font-semibold text-white hover:bg-red-700"
                >
                  Yes
                </button>
                <button type="button" onClick={() => setConfirming(false)} className="min-h-[32px] px-1.5 text-xs text-gray-600">
                  No
                </button>
              </span>
            ) : (
              <>
                {' · '}
                <button
                  type="button"
                  aria-label={`Delete the ${time} water check`}
                  className="font-semibold text-red-700"
                  onClick={() => setConfirming(true)}
                >
                  delete
                </button>
              </>
            ))}
        </span>
      )}
    </div>
  );
}

function WaterCheckDialog({
  open,
  onClose,
  openKey,
  farmId,
  crop,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  openKey: number;
  farmId: string;
  crop: Crop;
  editing: WaterCheckResponse | null;
}) {
  const { create, update } = useWaterCheckMutations(farmId, crop.id);
  const mutation = editing ? update : create;
  const close = () => {
    create.reset();
    update.reset();
    onClose();
  };
  // Closing mid-request would drop the pending state while the save still lands. A save queued
  // offline is different: it only goes out on reconnect (and refreshes the journal then), so closing is fine.
  const requestClose = () => {
    if (mutation.isPending && !mutation.isPaused) return;
    close();
  };
  return (
    <Dialog open={open} onClose={requestClose} title={editing ? 'Edit water check' : 'Water check'} size="lg">
      <WaterCheckForm
        key={`${openKey}-${editing?.id ?? 'new'}`}
        crop={crop}
        initial={editing ?? undefined}
        busy={mutation.isPending}
        paused={mutation.isPaused}
        error={mutation.error}
        submitLabel={editing ? 'Save changes' : 'Save check'}
        onCancel={requestClose}
        onSubmit={({ measured_at, measuredAtChanged, values }) =>
          editing
            ? update.mutate({ id: editing.id, body: { ...(measuredAtChanged ? { measured_at } : {}), ...values } }, { onSuccess: close })
            : create.mutate({ cycle_id: crop.id, measured_at, ...values }, { onSuccess: close })
        }
      />
    </Dialog>
  );
}

interface PrepWaterRow {
  key: string;
  label: string;
  unit?: string;
  columns: string[];
  /** Shown as a pair (a/b); the second number is part of the same entry. */
  show: (values: Array<number | null>, legacy: string | null) => string | null;
}

const single = (key: string): PrepWaterRow => {
  const p = GRID_PARAMS.find((x) => x.key === key)!;
  return { key, label: p.label, unit: p.unit, columns: [p.dbColumn], show: ([v]) => (v === null ? null : String(v)) };
};
const PREP_WATER: PrepWaterRow[] = [
  single('ph'),
  single('salinity'),
  single('transparency'),
  { key: 'alkalinity', label: 'Alkalinity', unit: 'ppm', columns: ['alkalinity_ppm', 'alkalinity_2'], show: ([a, b]) => formatPair(a, b) },
  { key: 'calMag', label: 'Cal/Mag', columns: ['calcium_ppm', 'magnesium_ppm'], show: ([a, b], legacy) => formatCalMag(a, b, legacy) },
  single('dissolvedOxygen'),
  single('temperature'),
  single('ammonia'),
];

function PrepWater({ farmId, pondId, crop }: { farmId: string; pondId: string; crop: Crop }) {
  const { data: readings = [] } = usePrepReadings(farmId, crop);
  const { checks } = useWaterChecks(farmId, crop.id);
  const today = todayInPH();
  const toNum = (raw: unknown) => (raw === null || raw === undefined || raw === '' ? null : Number(raw));
  const latest = PREP_WATER.flatMap((row) => {
    const found: Array<{ text: string; clear: boolean; at: string; label: string }> = [];
    const add = (values: Array<number | null>, legacy: string | null, at: string, label: string, clearAs: string | null = null) => {
      const text = clearAs ?? row.show(values, legacy);
      if (text !== null) found.push({ text, clear: clearAs !== null, at, label });
    };
    for (const r of readings) {
      const v = readingToValues(r);
      add(
        row.columns.map((c) => (typeof v[c] === 'number' ? (v[c] as number) : null)),
        typeof v.calmag === 'string' ? v.calmag : null,
        `${r.reading_date}T${slotClock(r.reading_slot)}`,
        r.reading_date === today ? r.reading_slot : `${fmt(r.reading_date)} ${r.reading_slot}`,
        row.key === 'transparency' && isClearToBottom(v[row.columns[0]]) ? clearText(v.depth_m) : null
      );
    }
    for (const c of checks) {
      const raw = c as unknown as Record<string, string | number | null | undefined>;
      const day = checkDay(c);
      const time = displayPhTime(c.measured_at);
      add(
        row.columns.map((col) => toNum(raw[col])),
        typeof raw.calmag === 'string' ? raw.calmag : null,
        phDateTimeLocal(new Date(c.measured_at)),
        day === today ? time : `${fmt(day)} ${time}`,
        row.key === 'transparency' && c.transparency_clear_to_bottom === true ? clearText(raw.depth_m) : null
      );
    }
    const top = found.sort((a, b) => b.at.localeCompare(a.at))[0];
    return top ? [{ row, ...top }] : [];
  });
  return (
    <Section labelledBy="ps-water" title="Water during prep" action={<TabLink pondId={pondId} tab="parameters">Parameters →</TabLink>}>
      {latest.length === 0 ? (
        <EmptyNote>No water checks logged during prep.</EmptyNote>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
          {latest.map(({ row, text, clear, label }) => (
            <div key={row.key} className="contents">
              <dt className="text-gray-500">{row.label}</dt>
              <dd className="tabular-nums">
                {text}
                {row.unit && !clear ? ` ${row.unit}` : ''} <span className="text-gray-500">· {label}</span>
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="text-xs text-gray-500">Log a check from the prep journal. The 5-slot grid starts on stocking day.</p>
    </Section>
  );
}

function PrepSoFar({ farmId, crop }: { farmId: string; crop: Crop }) {
  const { data: summary } = useApplicationSummary(farmId, crop.id);
  const prepProducts = (summary?.products ?? []).filter((p) => (p.prep_entries ?? 0) > 0);
  const cancelled = cropPhase(crop) === 'closed' && !crop.stocking_date;
  const last = crop.stocking_date ? addPhDays(crop.stocking_date, -1) : todayInPH();
  const days = cancelled ? null : Math.max(daysBetween(crop.prep_start_date!, last) + 1, 0);
  const planned = crop.planned_stocking_date ? daysBetween(crop.prep_start_date!, crop.planned_stocking_date) : null;
  return (
    <Section labelledBy="ps-sofar" title={cropPhase(crop) === 'prep' ? 'Prep so far' : 'Prep totals'}>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px]">
        <dt className="text-gray-500">Days</dt>
        <dd className="tabular-nums">
          {days ?? 'cancelled'}
          {planned !== null && !crop.stocking_date ? ` (planned ${planned})` : ''}
        </dd>
        {prepProducts.map((p) => (
          <div key={`${p.product_id ?? p.product_name}|${p.unit ?? ''}`} className="contents">
            <dt className="text-gray-500">{p.product_name}</dt>
            <dd className="tabular-nums">
              {amountText(p.prep_amount, p.unit)} · {p.prep_entries} {p.prep_entries === 1 ? 'time' : 'times'}
            </dd>
          </div>
        ))}
        <dt className="text-gray-500">Cost</dt>
        <dd className="font-semibold">later · counts toward this crop</dd>
      </dl>
    </Section>
  );
}

interface MarkStockedDialogProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  pond: PondFacts;
  crop: Crop;
  /** Phone layout: 48px targets and 16px text (iOS doesn't zoom on focus). */
  phone?: boolean;
}

// stocking_count is a 32-bit INTEGER column.
const MAX_STOCKED_PIECES = 2147483647;

export function MarkStockedDialog({ open, onClose, farmId, pond, crop, phone }: MarkStockedDialogProps) {
  const queryClient = useQueryClient();
  const today = todayInPH();
  const [date, setDate] = useState(() => (crop.planned_stocking_date && crop.planned_stocking_date <= today ? crop.planned_stocking_date : today));
  const [pieces, setPieces] = useState('');
  const [plStage, setPlStage] = useState('');
  const [details, setDetails] = useState<StockingFieldText>(EMPTY_STOCKING_TEXT);
  const detailsParsed = parseStockingText(details);
  const area = pondAreaM2(pond.area_ha);
  const parsedPieces = parseTypedAmount(pieces, { integer: true });
  const piecesEmpty = 'value' in parsedPieces && parsedPieces.value === null;
  const count = 'value' in parsedPieces && parsedPieces.value !== null ? parsedPieces.value : NaN;
  const validCount = count > 0 && count <= MAX_STOCKED_PIECES;
  const piecesProblem =
    'error' in parsedPieces
      ? parsedPieces.error
      : piecesEmpty
        ? 'Enter how many pieces were stocked.'
        : count <= 0
          ? 'Whole number above 0, like 350000.'
          : count > MAX_STOCKED_PIECES
            ? `Pieces can't be more than ${MAX_STOCKED_PIECES.toLocaleString('en-US')}.`
            : null;
  const density = validCount && area ? count / area : null;

  const stock = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${farmId}/cycles/${crop.id}/stock`, {
        stocking_date: date,
        stocking_count: count,
        pl_stage: plStage.trim() || null,
        ...(detailsParsed.payload.pieces_ordered !== null ? { pieces_ordered: detailsParsed.payload.pieces_ordered } : {}),
        ...(detailsParsed.payload.ordered_allowance_pct !== null ? { ordered_allowance_pct: detailsParsed.payload.ordered_allowance_pct } : {}),
        ...(Object.keys(detailsParsed.payload.stocking_extras).length > 0 ? { stocking_extras: detailsParsed.payload.stocking_extras } : {}),
      }),
    onSuccess: () => {
      toast.success(`${pond.name} stocked. ${format(parseISO(date), 'MMM d')} is DOC 1.`);
      queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
      // A backdated stocking date moves entries between prep and grow-out.
      queryClient.invalidateQueries({ queryKey: qk.applications.crop(farmId, crop.id) });
      queryClient.invalidateQueries({ queryKey: qk.milestones.crop(farmId, crop.id) });
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      queryClient.invalidateQueries({ queryKey: ['cycles', farmId] });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      onClose();
    },
  });

  const field = cn('w-full rounded-lg border border-gray-300 px-3', phone ? 'min-h-12 text-base' : 'min-h-[40px] text-sm');
  const action = (primary?: boolean, tone?: 'stock') => cn(headerActionClass(primary, tone), phone && 'min-h-12 flex-1 justify-center text-base');
  return (
    <Dialog open={open} onClose={onClose} title={`Mark ${pond.name} as stocked`} size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (validCount && date && Object.keys(detailsParsed.errors).length === 0 && !stock.isPending) stock.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            Stocking date
            <input type="date" required aria-describedby="stock-date-hint" className={field} value={date} min={crop.prep_start_date ?? undefined} max={today} onChange={(e) => setDate(e.target.value)} />
          </label>
          <span id="stock-date-hint" className={cn('min-h-4 text-xs', date ? 'text-gray-500' : 'text-red-700')}>
            {date ? 'This day becomes DOC 1.' : 'Pick the stocking date.'}
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            Pieces stocked
            <input
              inputMode="numeric"
              required
              aria-describedby="stock-pieces-hint"
              aria-invalid={!piecesEmpty && !validCount ? true : undefined}
              className={cn(field, 'tabular-nums', !piecesEmpty && !validCount && 'border-red-500')}
              value={pieces}
              onChange={(e) => setPieces(e.target.value)}
              placeholder="e.g. 350000"
            />
          </label>
          <span id="stock-pieces-hint" className={cn('min-h-4 text-xs', piecesProblem && !piecesEmpty ? 'text-red-700' : 'text-gray-500')}>
            {piecesProblem ??
              (density ? `${densityText(density)} pcs/m² on ${Math.round(area!).toLocaleString()} m²` : 'Density is worked out from the pond area.')}
          </span>
        </div>
        <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
          PL stage
          <input className={field} value={plStage} onChange={(e) => setPlStage(e.target.value)} placeholder="Optional, e.g. PL12" />
        </label>
        <StockingFields
          text={details}
          onChange={(key: StockingFieldKey, value: string) => setDetails((t) => ({ ...t, [key]: value }))}
          count={validCount ? count : null}
          areaM2={area}
          idPrefix="mark-stocked"
          phone={phone}
        />
        {stock.isError && (
          <p role="alert" className="text-sm text-red-700">
            Not saved: {stock.error instanceof Error ? stock.error.message : 'try again'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={action()}>
            Cancel
          </button>
          <button type="submit" disabled={!validCount || !date || stock.isPending || Object.keys(detailsParsed.errors).length > 0} className={cn(action(false, 'stock'), 'disabled:opacity-50')}>
            {stock.isPending ? 'Saving…' : 'Mark as stocked'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

interface StartPrepDialogProps {
  open: boolean;
  onClose: () => void;
  onStarted: () => void;
  farmId: string;
  /** Only the name and id are used, so a pond list entry will do. */
  pond: Pick<PondFacts, 'id' | 'name'>;
  phone?: boolean;
}

export function StartPrepDialog({ open, onClose, onStarted, farmId, pond, phone }: StartPrepDialogProps) {
  const queryClient = useQueryClient();
  const today = todayInPH();
  const [waterIn, setWaterIn] = useState(today);
  const [planned, setPlanned] = useState('');

  const start = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${farmId}/cycles/preparing`, {
        pond_id: pond.id,
        prep_start_date: waterIn,
        planned_stocking_date: planned || null,
      }),
    onSuccess: () => {
      toast.success(`${pond.name} is preparing. ${format(parseISO(waterIn), 'MMM d')} is Prep day 1.`);
      queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      queryClient.invalidateQueries({ queryKey: ['cycles', farmId] });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      onStarted();
      onClose();
    },
  });

  const field = cn('w-full rounded-lg border border-gray-300 px-3', phone ? 'min-h-12 text-base' : 'min-h-[40px] text-sm');
  const action = (primary?: boolean) => cn(headerActionClass(primary), phone && 'min-h-12 flex-1 justify-center text-base');
  return (
    <Dialog open={open} onClose={onClose} title={`Start pre-stocking in ${pond.name}`} size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (waterIn && !start.isPending) start.mutate();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            Water in
            <input type="date" required aria-describedby="water-in-hint" className={field} value={waterIn} max={today} onChange={(e) => setWaterIn(e.target.value)} />
          </label>
          <span id="water-in-hint" className={cn('min-h-4 text-xs', waterIn ? 'text-gray-500' : 'text-red-700')}>
            {waterIn ? 'This day is Prep day 1. The crop counts DOC from stocking.' : 'Pick the water-in date.'}
          </span>
        </div>
        <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
          Planned stocking
          <input type="date" className={field} value={planned} min={waterIn} onChange={(e) => setPlanned(e.target.value)} />
          <span className="text-xs font-normal text-gray-500">Optional. It can change while the water gets ready.</span>
        </label>
        {start.isError && (
          <p role="alert" className="text-sm text-red-700">
            Not saved: {start.error instanceof Error ? start.error.message : 'try again'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={action()}>
            Cancel
          </button>
          <button type="submit" disabled={!waterIn || start.isPending} className={cn(action(true), 'disabled:opacity-50')}>
            {start.isPending ? 'Saving…' : 'Start pre-stocking'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function CancelPrepDialog({ open, onClose, farmId, pond, crop }: MarkStockedDialogProps) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const cancel = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${farmId}/cycles/${crop.id}/close`, { status: 'cancelled', notes: notes.trim() || null }),
    onSuccess: () => {
      toast.success(`Pre-stocking in ${pond.name} cancelled`);
      queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      queryClient.invalidateQueries({ queryKey: ['cycles', farmId] });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      onClose();
    },
  });
  return (
    <Dialog open={open} onClose={onClose} title={`Cancel pre-stocking in ${pond.name}?`} size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          cancel.mutate();
        }}
      >
        <p className="text-sm text-gray-600">
          The crop closes as cancelled and the pond can start again. Its prep journal stays viewable as a past crop.
        </p>
        <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
          Why
          <input className="min-h-[40px] w-full rounded-lg border border-gray-300 px-3 text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </label>
        {cancel.isError && (
          <p role="alert" className="text-sm text-red-700">
            Not saved: {cancel.error instanceof Error ? cancel.error.message : 'try again'}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={headerActionClass()}>
            Keep preparing
          </button>
          <button type="submit" disabled={cancel.isPending} className="min-h-[40px] rounded-lg bg-red-600 px-3.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
            {cancel.isPending ? 'Saving…' : 'Cancel pre-stocking'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
